import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { StorageService } from '@core/services/util/storage.service';
import { LoginService } from '@core/services/login/login.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { CustomerService } from '@core/services/customer/customer.service';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { SentryBreadcrumbsService } from '@app/core/services/sentry-breadcrumbs.service';
import { GtmService } from '@app/core/services/analytics/gtm.service';

import { BaseComponent } from '@core/components/base/base.component';
import { PonchoUIModule } from '@poncho/ui';
import { FormsModule } from '@angular/forms';
import { UtilityService } from '@app/core/services/util/utility.service';
import { FeatureToggleService } from '@app/core/services/util/feature-toggle.service';

@Component({
    selector: 'app-main-otp-verification',
    templateUrl: './main-otp-verification.component.html',
    styleUrls: ['./main-otp-verification.component.scss'],
    standalone: true,
    imports: [FormsModule, PonchoUIModule]
})
export class MainOtpVerificationComponent extends BaseComponent {
    @Input() loginWithPassword: boolean;
    @Output() closeDialog = new EventEmitter();
    @ViewChild('otpInput') otpInput: ElementRef;
    btnLoading = false;
    mobile_number: string;
    enable_resend_otp = false;
    time_left: any;
    inputs = [];
    abortController: AbortController;
    resend_otp_timer: any;

    otp = '';
    otpArray = new Array(6).fill('');

    constructor(
        private storageService: StorageService,
        private loginService: LoginService,
        private toastrService: ToastrService,
        private customerService: CustomerService,
        private authenticationService: AuthenticationService,
        private cdr: ChangeDetectorRef,
        private breadCrumb: SentryBreadcrumbsService,
        private gtmService: GtmService,
        private utilityService: UtilityService,
        private featureToggleService: FeatureToggleService
    ) {
        super();
        this.mobile_number = this.storageService.get('phone_number');
        const autoreadOTPEnabled = this.featureToggleService.isEnabled('autoreadOTP');
        if (autoreadOTPEnabled) {
            this.autoReadSMS();
        }
        this.initiateResendOTPTimer();
    }

    ngOnDestroy(): void {
        this.abortController?.abort('Component Destroyed');
    }

    autoReadSMS() {
        if ('OTPCredential' in window) {
            if (navigator.credentials) {
                try {
                    this.abortController = new AbortController();

                    // used AbortController with setTimeout so that WebOTP API (Autoread sms) will get disabled after 1min
                    setTimeout(() => {
                        this.abortController?.abort('Timeout Autoread');
                    }, 60000);

                    const config = {
                        otp: { transport: ['sms'] },
                        signal: this.abortController.signal
                    };
                    navigator.credentials
                        .get(config)
                        .then((content: any) => {
                            if (content && content.code) {
                                this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'Code Copied');
                                this.autoFill(content.code);
                            } else {
                                // need proper logging if we get below logs
                                this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'Resolved but failed: ' + content);
                            }
                        })
                        .catch(err => {
                            this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'Autoread OTP Failed: ' + err);
                        })
                        .finally(() => {
                            this.abortController.abort('Manually aborting');
                        });
                } catch (err) {
                    this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'Parse error: ' + err);
                }
            } else {
                this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'navigator.credentials not supported');
            }
        } else {
            this.gtmService.logWebAnalyticsEvent('Autoread OTP', 'Autoread feature not present');
        }
    }

    generateOtp(login_method: string) {
        if (!this.enable_resend_otp) {
            return;
        }

        //TO DO: abort for whatsapp OTP
        // if(login_method === 'whatsapp_otp') {
        //     this.abortController?.abort();
        // }

        this.enable_resend_otp = false;
        this.initiateResendOTPTimer();

        this.loginService.ensureSessionId().then(() => {
            lastValueFrom(this.loginService.generateOtp(login_method))
                .then((data: any) => {
                    if (data && data.meta) {
                        this.toastrService.openSnackBar(data.meta.message, 'OKAY');
                    }
                })
                .catch((error: any) => {
                    this.toastrService.openSnackBar(error?.meta?.message, 'OKAY');
                });
        });
    }

    initiateResendOTPTimer() {
        this.time_left = 30;
        let time_left = 30;

        clearInterval(this.resend_otp_timer);

        this.resend_otp_timer = setInterval(() => {
            if (time_left > 0) {
                time_left--;
                this.time_left = ('0' + time_left).slice(-2);
            } else {
                this.enable_resend_otp = true;
                clearInterval(this.resend_otp_timer);
            }
            this.cdr.detectChanges();
        }, 1000);
    }

    loginWPassword() {
        this.authenticationService.mobileStep.next('login-with-password');
    }

    changeMobileNumber() {
        this.authenticationService.nextStep('add-number');
    }

    signIn() {
        const otp = this.otp;

        if (!this.isValidOTP(otp)) {
            this.toastrService.openSnackBar('Please provide valid OTP.', 'OKAY');
            return;
        }

        this.btnLoading = true;

        this.loginService.ensureSessionId().then(() => {
            this.loginService
                .signinWithOtp(otp)
                .toPromise()
                .then((data: any) => {
                    if (data && data.meta) {
                        if (data.meta.code === 200) {
                            this.breadCrumb.add('auth', 'MainOtpVerificationComponent Success 200', null);
                            this.customerService.setAuthTokenAndFetchDetails(data);
                            this.authenticationService.closeWrapperDialog.next({ authentication: true });
                        } else {
                            this.breadCrumb.add('auth', 'MainOtpVerificationComponent Success !200', { data: data });
                            this.toastrService.openSnackBar(data.meta.message, 'OKAY');
                        }
                    }
                })
                .catch((error: any) => {
                    this.breadCrumb.add('auth', 'MainOtpVerificationComponent Error', { data: error });
                    this.toastrService.openSnackBar(error?.meta?.message, 'OKAY');
                })
                .finally(() => {
                    this.btnLoading = false;
                    this.cdr.detectChanges();
                });
        });
    }

    isValidOTP(otp: string) {
        const regex = /^[0-9]{6}$/;
        return regex.test(otp);
    }

    autoFill(data: string) {
        if (this.isValidOTP(data)) {
            this.otp = data;
            this.formatOtp();
        } else {
            this.toastrService.openSnackBar('Please provide valid OTP.', 'OKAY');
        }
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedText = event.clipboardData.getData('text');
        this.autoFill(pastedText);
    }

    allowOnlyNumbers(event: KeyboardEvent): boolean {
        return this.utilityService.allowOnlyNumbers(event);
    }

    formatOtp() {
        this.otp = this.otp.replace(/\D/g, '').slice(0, 6); // Only allow numbers, max 6 digits
        this.otpArray = this.otp.split('').concat(new Array(6 - this.otp.length).fill(''));
    }

    setCursorToEnd() {
        const input = this.otpInput.nativeElement;
        input.setSelectionRange(this.otp.length, this.otp.length);
    }

    onBeforeInput(event: InputEvent) {
        // Prevent non-numeric input at the system level
        if (event.data && !/^\d$/.test(event.data)) {
            event.preventDefault();
        }
    }
}
